// Shared state and helpers of the mail editor (docs/tickable-mail.md §5, §5c, §8).
import { inject, toRaw, type InjectionKey, type Ref, type ComputedRef } from 'vue'
import type { Block, BlockType, Brand, L, MailDocument } from '~~/supabase/functions/_shared/mail/blocks'
import { ORDER_BLOCKS, sourceHash, textOf } from '~~/supabase/functions/_shared/mail/blocks'

export interface EditorContext {
  lang: Ref<string>
  source: ComputedRef<string>
  readOnly: ComputedRef<boolean>
  organizationId: ComputedRef<string | undefined>
  brand: ComputedRef<Brand>
  orgName: ComputedRef<string>
  // retranslates one field into the current language
  retranslate: (l: L) => Promise<void>
}
export const EDITOR_KEY: InjectionKey<EditorContext> = Symbol('mail-editor')

export const useEditorContext = () => inject(EDITOR_KEY)!

export const newId = () => crypto.randomUUID().slice(0, 8)

export const BLOCK_ICONS: Record<BlockType, string> = {
  heading: 'heading',
  text: 'align-left',
  image: 'image',
  button: 'arrow-pointer',
  divider: 'minus',
  spacer: 'arrows-up-down',
  event: 'calendar-day',
  tickets: 'ticket',
  payment_link: 'credit-card',
  order_link: 'receipt',
  extras: 'gift',
  shop_button: 'store',
  event_promo: 'calendar-plus',
  discount_code: 'tag',
}

export const BLOCK_TYPES = Object.keys(BLOCK_ICONS) as BlockType[]

// A marketing mail has no order behind it; a mail about an order (a service message too) carries
// no promotion, and render.ts drops event_promo there anyway.
// discount_code ("Persoonlijke code") only in an invitation's mail (kind 'invitation'): only it has a discount to make codes of.
export const blockTypesFor = (kind: string) =>
  kind === 'marketing' || kind === 'invitation'
    ? BLOCK_TYPES.filter((type) => !ORDER_BLOCKS.includes(type) && (type !== 'discount_code' || kind === 'invitation'))
    : BLOCK_TYPES.filter((type) => type !== 'event_promo' && type !== 'discount_code')

export const newBlock = (type: BlockType): Block => {
  const id = newId()
  switch (type) {
    case 'heading': return { id, type, text: {}, level: 1 }
    case 'text': return { id, type, html: {} }
    case 'image': return { id, type, src: '', alt: {} }
    case 'button': return { id, type, label: {}, href: 'https://' }
    case 'spacer': return { id, type, size: 'm' }
    case 'event_promo': return { id, type, mode: 'upcoming', max: 3 }
    // empty: the campaign's own discount
    case 'discount_code': return { id, type, discount_id: '' }
    case 'payment_link':
    case 'order_link':
    case 'extras':
    case 'shop_button': return { id, type }
    default: return { id, type } as Block
  }
}

export const cloneBlock = (block: Block): Block => ({ ...structuredClone(toRaw(block)), id: newId() })

// What a translation in `lang` looks like next to the source text (§5c).
export type FieldState = 'none' | 'missing' | 'ai' | 'stale' | 'manual'

export const fieldState = (l: L | undefined, lang: string, source: string): FieldState => {
  if (lang === source) return 'none'
  const sourceText = textOf(l?.[source])
  const value = l?.[lang]
  if (value == null || !textOf(value).trim()) return sourceText.trim() ? 'missing' : 'none'
  if (typeof value === 'string' || value.origin !== 'ai') return 'manual'
  return value.source_hash === sourceHash(sourceText) ? 'ai' : 'stale'
}

// Typing in a field: plain text in the source language, a manual translation elsewhere.
export const writeText = (l: L, lang: string, source: string, text: string) => {
  if (lang === source) l[lang] = text
  else if (!text.trim()) delete l[lang]
  else l[lang] = { text, origin: 'manual' }
}

export const writeTranslation = (l: L, lang: string, source: string, text: string) => {
  l[lang] = { text, source_hash: sourceHash(textOf(l[source])), origin: 'ai' }
}

// Every translatable field of a document by the path collectTexts uses.
export const lFields = (doc: MailDocument): Record<string, L> => {
  const out: Record<string, L> = {}
  out.subject = doc.subject
  if (doc.preheader) out.preheader = doc.preheader
  for (const block of doc.blocks) {
    switch (block.type) {
      case 'heading': out[`${block.id}.text`] = block.text; break
      case 'text': out[`${block.id}.html`] = block.html; break
      case 'image': out[`${block.id}.alt`] = block.alt; break
      case 'tickets':
      case 'payment_link':
        if (block.title) out[`${block.id}.title`] = block.title
        if (block.label) out[`${block.id}.label`] = block.label
        if (block.note) out[`${block.id}.note`] = block.note
        if (block.type === 'tickets' && block.pdf_label) out[`${block.id}.pdf_label`] = block.pdf_label
        break
      case 'button':
      case 'order_link':
      case 'shop_button': if (block.label) out[`${block.id}.label`] = block.label; break
      case 'event_promo': if (block.title) out[`${block.id}.title`] = block.title; break
      case 'extras':
        if (block.title) out[`${block.id}.title`] = block.title
        if (block.label) out[`${block.id}.label`] = block.label
        break
      case 'discount_code':
        if (block.title) out[`${block.id}.title`] = block.title
        if (block.label) out[`${block.id}.label`] = block.label
        if (block.note) out[`${block.id}.note`] = block.note
        break
    }
  }
  return out
}

export const isValidHref = (href: string) =>
  /^(https:\/\/[^\s]+|mailto:[^\s]+)$/i.test(href.trim()) || /^\{\{\s*[a-z_.]+\s*\}\}$/i.test(href.trim())

// Problems per block id ('subject' and 'doc' for the rest), as i18n keys.
export const validate = (doc: MailDocument, source: string, required: BlockType[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  const add = (id: string, key: string) => (errors[id] ??= []).push(key)
  if (!textOf(doc.subject?.[source]).trim()) add('subject', 'mail_editor_error_subject')
  for (const type of required)
    if (!doc.blocks.some((b) => b.type === type)) add('doc', `mail_editor_error_required_${type}`)
  for (const block of doc.blocks) {
    switch (block.type) {
      case 'heading':
        if (!textOf(block.text[source]).trim()) add(block.id, 'mail_editor_error_heading')
        break
      case 'image':
        if (!/^https:\/\//.test(block.src)) add(block.id, 'mail_editor_error_image_src')
        if (!textOf(block.alt[source]).trim()) add(block.id, 'mail_editor_error_image_alt')
        if (block.href && !isValidHref(block.href)) add(block.id, 'mail_editor_error_href')
        break
      case 'button':
        if (!textOf(block.label[source]).trim()) add(block.id, 'mail_editor_error_button_label')
        if (!isValidHref(block.href)) add(block.id, 'mail_editor_error_href')
        break
      case 'event_promo':
        if (block.mode === 'pick' && !block.event_ids?.length) add(block.id, 'mail_editor_error_promo_events')
        break
    }
  }
  return errors
}

// The same colours render.ts uses, so the canvas looks like the mail.
// shop themes store colours without '#' (e.g. 1b1f1e)
const safeColor = (value: string | null | undefined, fallback: string) => {
  const v = (value ?? '').trim().replace(/^#?/, '#')
  return /^#[0-9a-f]{3,8}$/i.test(v) ? v : fallback
}

const onColor = (hex: string) => {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6)
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? '#111111' : '#ffffff'
}

const mutedOn = (hex: string, light = '#666666') => (onColor(hex) === '#ffffff' ? '#a3a3a3' : light)

export const brandColors = (brand: Brand) => {
  const accent = safeColor(brand.accent, '#1f1f1f')
  const background = safeColor(brand.background, '#f4f2ee')
  const card = safeColor(brand.card, '#ffffff')
  return {
    accent,
    onAccent: onColor(accent),
    background,
    card,
    muted: mutedOn(card),
    soft: mutedOn(card, '#555555'),
    footerMuted: mutedOn(background),
    ink: safeColor(brand.text, '#1f1f1f'),
    font: `${brand.font || 'Inter'}, Helvetica, Arial, sans-serif`,
  }
}
